import numeral from 'numeral';

import {hasValue} from '@famly/stat_ts-utils_has-value';

import type {PlanInput} from './types/manage-plan-types';

interface PricingGroup {
    pricingGroupId: string;
    deleted: boolean;
}

/**
 * Auto detect the `pricingGroupId` from a list of pricing groups
 *
 * This is possible when the "Automatic" pricing group option is unavailable (i.e.
 * there are no pricing group move rules setup) and when there's only one active
 * pricing (i.e. non-deleted) group to choose from.
 *
 * @param canChooseAutoPricingGroup
 * @param pricingGroups
 */
export const getAutoPricingGroupId = (
    canChooseAutoPricingGroup: boolean,
    pricingGroups: Array<PricingGroup>,
): string | null => {
    if (canChooseAutoPricingGroup) {
        return null;
    }

    const activePricingGroups = pricingGroups.filter(pricingGroup => !pricingGroup.deleted);

    return activePricingGroups.length === 1 ? activePricingGroups[0].pricingGroupId : null;
};

export const formatValue = (value: number | string | null): string | null => {
    if (!hasValue(value) || Number.isNaN(value)) {
        return null;
    }

    return typeof value === 'number' ? value.toFixed(2) : value;
};

// todo: add proper type for plan
export const mapPlanInput = (plan: any, oldId?: string): PlanInput => {
    return {
        childId: plan.childId,
        oldPlanId: oldId && oldId !== '' ? oldId : undefined,
        note: plan.note,
        planParts: plan.planParts.map(part => {
            const publicFundingSettings = plan.publicFundingSettings
                ? {
                      fundingTypeIds: plan.publicFundingSettings.fundingTypeIds,
                      amount: formatValue(plan.publicFundingSettings?.amount),
                      maxMinutesPerDay: plan.publicFundingSettings.maxFundedMinutes,
                      method: plan.publicFundingSettings.method,
                      period: plan.publicFundingSettings.period,
                      rate: formatValue(plan.publicFundingSettings?.rate),
                      time: {
                          hours: plan.publicFundingSettings.hours,
                          minutes: plan.publicFundingSettings.minutes,
                      },
                  }
                : null;

            return {
                attendanceScheduleId: part.attendanceScheduleId,
                billingProfileId: part.billingProfileId,
                billingScheme: part.billing.id,
                invoices: part.billing.invoices,
                termTimeOnly: part.termTimeOnly,
                weeksOfCare: part.billing.weeksOfCare,
                ordering: part.ordering,
                priceModifiers: part.discounts.map(discount => {
                    if (discount.title === 'Surcharge') {
                        return {
                            surcharge: {
                                amount: Math.abs(discount.amount),
                                percent: discount.isPercent,
                                period: discount.period,
                                showOnInvoice: discount.showOnInvoice,
                                title: discount.title,
                                ordering: discount.ordering,
                            },
                        };
                    }
                    if (discount.origin === 'preset') {
                        return {
                            presetDiscount: {
                                discountPresetId: discount.discountPresetId,
                                period: discount.period,
                                ordering: discount.ordering,
                            },
                        };
                    }
                    return {
                        discount: {
                            amount: discount.amount,
                            percent: discount.isPercent,
                            period: discount.period,
                            showOnInvoice: discount.showOnInvoice,
                            title: discount.title,
                            ordering: discount.ordering,
                        },
                    };
                }),
                publicFundingSettings,
                sessionBookings: part.sessionBookings.map(sessionBooking => ({
                    allowFunding: sessionBooking.fundable ?? true, // Response will return null (if true) or false
                    dayOfWeek: sessionBooking.day,
                    range: sessionBooking.start
                        ? {
                              from: sessionBooking.start,
                              to: sessionBooking.end,
                          }
                        : null,
                    sessionId: sessionBooking.sessionId,
                    planRuleId: sessionBooking.planRuleId,
                })),
                productBookings: part.productBookings.map(productBooking => ({
                    amount: productBooking.amount,
                    dayOfWeek: productBooking.day,
                    productId: productBooking.productId,
                    bookedPrice: hasValue(productBooking.bookedPrice)
                        ? numeral(Number(productBooking.bookedPrice)).format('0.00')
                        : productBooking.bookedPrice,
                    planRuleId: productBooking.planRuleId,
                    bookingStatus: productBooking.bookingStatus,
                })),
            };
        }),
        pricing: {
            ruleGroupId: plan.ruleGroupId ?? null,
            pricingGroupId: plan.pricingGroupId ?? null,
        },
        range: {
            from: plan.from,
            to: plan.to,
        },
    };
};
